import { useEffect, useMemo, useRef, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import {
  ArchiveRestore,
  BookOpen,
  BrainCircuit,
  Check,
  ChevronLeft,
  FileAudio,
  FileText,
  Gauge,
  Headphones,
  Library,
  Menu,
  Mic2,
  Plus,
  Search,
  Settings,
  Sparkles,
  Trash2,
  WifiOff,
  X,
} from 'lucide-react'
import { FileManager } from './components/FileManager'
import { RecorderPanel } from './components/RecorderPanel'
import { TextToSpeech } from './components/TextToSpeech'
import { getOrCreateSession, isCloudConfigured, supabase } from './lib/supabase'
import { mergeTranscripts, splitAudioForTranscription } from './services/audioService'
import { processText, transcribeAudio } from './services/apiService'
import {
  LOCAL_USER_ID,
  createLesson,
  listLessonFiles,
  listLessons,
  saveLesson,
  syncPendingOperations,
  uploadLessonFile,
} from './services/lessonService'
import type { AiMaterial, Lesson, LessonFile } from './types'

type Tab = 'overview' | 'record' | 'text' | 'notes' | 'files' | 'listen' | 'books' | 'trash' | 'settings'
type TranscriptionModel = 'gpt-4o-mini-transcribe' | 'gpt-4o-transcribe'

const navItems: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Сабақтар', icon: <Library size={19} /> },
  { id: 'record', label: 'Аудио жазу', icon: <Mic2 size={19} /> },
  { id: 'text', label: 'Толық мәтін', icon: <FileText size={19} /> },
  { id: 'notes', label: 'Конспект', icon: <BrainCircuit size={19} /> },
  { id: 'files', label: 'Файлдар', icon: <FileAudio size={19} /> },
  { id: 'books', label: 'PDF кітаптар', icon: <BookOpen size={19} /> },
  { id: 'listen', label: 'Мәтінді тыңдау', icon: <Headphones size={19} /> },
  { id: 'trash', label: 'Себет', icon: <Trash2 size={19} /> },
  { id: 'settings', label: 'Баптаулар', icon: <Settings size={19} /> },
]

const App = () => {
  const [session, setSession] = useState<Session | null>(null)
  const [authReady, setAuthReady] = useState(!isCloudConfigured)
  const [authError, setAuthError] = useState('')
  const [authRetry, setAuthRetry] = useState(0)
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [selected, setSelected] = useState<Lesson | null>(null)
  const [files, setFiles] = useState<LessonFile[]>([])
  const [tab, setTab] = useState<Tab>('overview')
  const [search, setSearch] = useState('')
  const [mobileNav, setMobileNav] = useState(false)
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')
  const [language, setLanguage] = useState(localStorage.getItem('sabak-language') || 'kk')
  const [model, setModel] = useState<TranscriptionModel>(
    (localStorage.getItem('sabak-model') as TranscriptionModel) || 'gpt-4o-mini-transcribe',
  )
  const [autoTranscribe, setAutoTranscribe] = useState(localStorage.getItem('sabak-auto-transcribe') === 'true')
  const [pdfText, setPdfText] = useState('')
  const [pdfName, setPdfName] = useState('')
  const [pdfPages, setPdfPages] = useState<string[]>([])
  const [pdfPage, setPdfPage] = useState(1)
  const [pdfSearch, setPdfSearch] = useState('')
  const [pdfQuestion, setPdfQuestion] = useState('')
  const [pdfAnswer, setPdfAnswer] = useState('')
  const saveTimer = useRef<number | null>(null)
  const userId = session?.user.id || LOCAL_USER_ID

  useEffect(() => {
    if (!supabase) return
    let active = true

    setAuthReady(false)
    setAuthError('')
    getOrCreateSession()
      .then((nextSession) => {
        if (!active) return
        setSession(nextSession)
        setAuthReady(true)
      })
      .catch((reason) => {
        if (!active) return
        setAuthError(
          reason instanceof Error
            ? reason.message
            : 'Қонақ режимін ашу мүмкін болмады.',
        )
        setAuthReady(true)
      })

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!active) return
      setSession(nextSession)
      setAuthReady(true)
    })
    return () => {
      active = false
      data.subscription.unsubscribe()
    }
  }, [authRetry])

  const refreshLessons = async () => {
    setLoading(true)
    try {
      const next = await listLessons()
      setLessons(next)
      setSelected((current) => next.find((lesson) => lesson.id === current?.id) || next.find((lesson) => !lesson.deleted_at) || null)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Сабақтар жүктелмеді')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!authReady || (isCloudConfigured && !session)) return
    refreshLessons()
    syncPendingOperations().then((count) => count && setStatus(`${count} offline өзгеріс бұлтпен синхрондалды.`))
    const online = () =>
      syncPendingOperations().then((count) => {
        if (count) return refreshLessons()
      })
    window.addEventListener('online', online)
    return () => window.removeEventListener('online', online)
  }, [authReady, session?.user.id])

  const refreshFiles = async () => {
    if (!selected) {
      setFiles([])
      return
    }
    setFiles(await listLessonFiles(selected.id))
  }

  useEffect(() => {
    refreshFiles()
  }, [selected?.id])

  const updateSelected = (patch: Partial<Lesson>) => {
    if (!selected) return
    const updated = { ...selected, ...patch, updated_at: new Date().toISOString() }
    setSelected(updated)
    setLessons((current) => current.map((lesson) => (lesson.id === updated.id ? updated : lesson)))
    setStatus('Автоматты сақтау...')
    if (saveTimer.current) window.clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(async () => {
      const saved = await saveLesson(updated)
      setStatus(navigator.onLine || !isCloudConfigured ? 'Сақталды' : 'Offline: құрылғыда қауіпсіз сақталды')
      setLessons((current) => current.map((lesson) => (lesson.id === saved.id ? saved : lesson)))
    }, 700)
  }

  const addLesson = async () => {
    const lesson = await createLesson(userId)
    setLessons((current) => [lesson, ...current])
    setSelected(lesson)
    setTab('record')
    setMobileNav(false)
  }

  const trashLesson = () => {
    if (!selected || !window.confirm(`«${selected.title}» сабағын себетке жібереміз бе? Кейін қалпына келтіруге болады.`)) return
    updateSelected({ deleted_at: new Date().toISOString() })
    setTab('overview')
  }

  const restoreLesson = (lesson: Lesson) => {
    const restored = { ...lesson, deleted_at: null, updated_at: new Date().toISOString() }
    setSelected(restored)
    setLessons((current) => current.map((item) => (item.id === restored.id ? restored : item)))
    saveLesson(restored).then(() => setStatus('Сабақ қалпына келтірілді'))
    setTab('overview')
  }

  const uploadRecording = async (blob: Blob, fileName: string) => {
    if (!selected) return
    setStatus('Аудио бұлтқа сақталуда...')
    await uploadLessonFile(selected.id, userId, blob, fileName)
    await refreshFiles()
    setStatus('Бастапқы аудио қауіпсіз сақталды')
    if (autoTranscribe) await directTranscribe(blob, fileName)
  }

  const directTranscribe = async (blob: Blob, fileName: string) => {
    if (!selected) return
    setError('')
    try {
      const chunks = await splitAudioForTranscription(blob)
      const results: string[] = []
      for (let index = 0; index < chunks.length; index += 1) {
        setStatus(`${index + 1}/${chunks.length} бөлік мәтінге айналдырылуда`)
        const result = await transcribeAudio(
          chunks[index],
          chunks[index].type.includes('wav') ? `part-${index + 1}.wav` : fileName,
          language,
          selected.id,
          model,
          results.at(-1)?.slice(-500) || '',
        )
        results.push(result.text)
      }
      updateSelected({ transcript: mergeTranscripts(results) })
      setTab('text')
      setStatus('Толық мәтін дайын')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Транскрипция қатесі')
    }
  }

  const applyAi = async (action: 'clean' | 'full-summary' | 'short-summary' | 'quiz' | 'flashcards' | 'explain') => {
    if (!selected?.transcript.trim()) {
      setError('Алдымен аудионы мәтінге айналдырыңыз немесе мәтінді қолмен енгізіңіз.')
      return
    }
    setError('')
    setStatus('AI материал дайындап жатыр...')
    try {
      const result: AiMaterial = await processText(selected.cleaned_transcript || selected.transcript, action, language)
      const patch: Partial<Lesson> = {}
      if (result.cleaned_transcript) patch.cleaned_transcript = result.cleaned_transcript
      if (result.full_summary) patch.summary = result.full_summary
      if (result.short_summary) patch.short_summary = result.short_summary
      if (result.key_points) patch.key_points = result.key_points
      if (result.terms) patch.terms = result.terms
      if (result.quiz) patch.quiz = result.quiz
      if (result.flashcards) patch.flashcards = result.flashcards
      if (result.simple_explanation) patch.summary = result.simple_explanation
      updateSelected(patch)
      setStatus('AI нәтижесі сақталды')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'AI өңдеу қатесі')
    }
  }

  const summarizePdfSection = async () => {
    if (!selected || !displayedPdfText.trim()) return
    setStatus('PDF бөлімінен конспект жасалуда...')
    setError('')
    try {
      const result = await processText(displayedPdfText, 'full-summary', language)
      updateSelected({
        transcript: displayedPdfText,
        summary: result.full_summary || '',
        key_points: result.key_points || [],
        terms: result.terms || [],
      })
      setTab('notes')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'PDF конспект қатесі')
    }
  }

  const askPdf = async () => {
    if (!displayedPdfText.trim() || !pdfQuestion.trim()) return
    setStatus('Кітаптан жауап ізделуде...')
    setError('')
    try {
      const result = await processText(displayedPdfText, 'book-question', language, pdfQuestion)
      setPdfAnswer(result.answer || 'Бұл сұраққа кітап мәтінінен жауап табылмады.')
      setStatus('Жауап дайын')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Кітап бойынша сұрақ қатесі')
    }
  }

  const activeLessons = lessons.filter((lesson) => !lesson.deleted_at)
  const deletedLessons = lessons.filter((lesson) => lesson.deleted_at)
  const filteredLessons = activeLessons.filter((lesson) =>
    `${lesson.subject} ${lesson.title} ${lesson.transcript}`.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  )
  const displayedPdfText = useMemo(() => {
    if (!pdfSearch.trim()) return pdfPages[pdfPage - 1] || pdfText
    return pdfText
      .split('\n')
      .filter((line) => line.toLocaleLowerCase().includes(pdfSearch.toLocaleLowerCase()))
      .join('\n')
  }, [pdfText, pdfPages, pdfPage, pdfSearch])

  if (!authReady) return <div className="loading-screen"><div className="brand-mark"><Sparkles /></div><p>Сабақ AI ашылуда...</p></div>
  if (isCloudConfigured && !session) {
    return (
      <div className="loading-screen">
        <div className="brand-mark"><Sparkles /></div>
        <h2>Қонақ режимі ашылмады</h2>
        <p>{authError || 'Интернет байланысын тексеріп, қайта көріңіз.'}</p>
        <button className="primary" onClick={() => setAuthRetry((value) => value + 1)}>
          Қайта ашу
        </button>
      </div>
    )
  }

  const renderContent = () => {
    if (tab === 'settings') {
      return (
        <section className="content-page">
          <div className="page-title"><div><p className="eyebrow">БАПТАУЛАР</p><h2>Жұмыс режимі</h2></div></div>
          <div className="panel form-grid settings-grid">
            <label>Транскрипция тілі<select value={language} onChange={(event) => { setLanguage(event.target.value); localStorage.setItem('sabak-language', event.target.value) }}><option value="">Автоматты анықтау</option><option value="kk">Қазақша</option><option value="ru">Орысша</option><option value="en">Ағылшынша</option></select></label>
            <label>Whisper моделі<select value={model} onChange={(event) => { const value = event.target.value as TranscriptionModel; setModel(value); localStorage.setItem('sabak-model', value) }}><option value="gpt-4o-mini-transcribe">Жылдам және үнемді</option><option value="gpt-4o-transcribe">Жоғары сапа</option></select></label>
            <label className="toggle-row"><span><strong>Автоматты транскрипция</strong><small>Жазу тоқтаған соң бірден бастау</small></span><input type="checkbox" checked={autoTranscribe} onChange={(event) => { setAutoTranscribe(event.target.checked); localStorage.setItem('sabak-auto-transcribe', String(event.target.checked)) }} /></label>
            <div className={`notice ${isCloudConfigured ? 'success' : 'warning'}`}>{isCloudConfigured ? 'Supabase cloud sync қосылған. Сабақтар басқа құрылғыда да көрінеді.' : 'Setup mode: Supabase мәндері енгізілмеген. Қазір дерек тек осы құрылғыдағы IndexedDB cache-та сақталады.'}</div>
          </div>
        </section>
      )
    }

    if (tab === 'trash') {
      return (
        <section className="content-page">
          <div className="page-title"><div><p className="eyebrow">SOFT DELETE</p><h2>Себет</h2></div><span className="counter">{deletedLessons.length}</span></div>
          <div className="lesson-grid">{deletedLessons.map((lesson) => <article className="lesson-card" key={lesson.id}><div className="subject-dot" /><p>{lesson.subject || 'Пәнсіз'}</p><h3>{lesson.title}</h3><button className="secondary" onClick={() => restoreLesson(lesson)}><ArchiveRestore size={17} /> Қалпына келтіру</button></article>)}</div>
          {!deletedLessons.length && <div className="empty-state panel"><Trash2 /><h3>Себет бос</h3></div>}
        </section>
      )
    }

    if (tab === 'overview') {
      return (
        <section className="content-page">
          <div className="page-title"><div><p className="eyebrow">ОҚУ КЕҢІСТІГІ</p><h2>Сабақтар</h2></div><button className="primary" onClick={addLesson}><Plus size={18} /> Жаңа сабақ</button></div>
          <div className="kpi-grid">
            <div className="kpi"><Library /><span><strong>{activeLessons.length}</strong><small>Барлық сабақ</small></span></div>
            <div className="kpi"><FileAudio /><span><strong>{files.filter((file) => file.file_type.startsWith('audio')).length}</strong><small>Аудио файл</small></span></div>
            <div className="kpi"><BrainCircuit /><span><strong>{activeLessons.filter((lesson) => lesson.summary).length}</strong><small>Дайын конспект</small></span></div>
          </div>
          <div className="search-box"><Search size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Пән, тақырып немесе мәтін бойынша іздеу..." /></div>
          <div className="lesson-grid">
            {filteredLessons.map((lesson) => (
              <button className={`lesson-card ${selected?.id === lesson.id ? 'selected' : ''}`} key={lesson.id} onClick={() => { setSelected(lesson); setTab('record') }}>
                <div className="subject-dot" /><p>{lesson.subject || 'Пәнсіз'}</p><h3>{lesson.title}</h3>
                <div className="lesson-footer"><span>{new Date(lesson.updated_at).toLocaleDateString('kk-KZ')}</span><span>{lesson.transcript ? <><Check size={14} /> Мәтін бар</> : 'Жаңа'}</span></div>
              </button>
            ))}
          </div>
          {!filteredLessons.length && <div className="empty-state panel"><Library /><h3>Сабақ табылмады</h3><p>Жаңа сабақ қосыңыз немесе іздеу сөзін өзгертіңіз.</p></div>}
        </section>
      )
    }

    if (!selected) {
      return <div className="empty-state panel"><Sparkles /><h2>Алдымен сабақ таңдаңыз</h2><button className="primary" onClick={addLesson}><Plus size={18} /> Жаңа сабақ</button></div>
    }

    return (
      <section className="content-page">
        <div className="lesson-header panel">
          <div className="lesson-inputs">
            <input className="title-input" value={selected.title} onChange={(event) => updateSelected({ title: event.target.value })} placeholder="Сабақ тақырыбы" />
            <input value={selected.subject} onChange={(event) => updateSelected({ subject: event.target.value })} placeholder="Пән атауы" />
          </div>
          <div className="save-state">{status || 'Барлық өзгеріс автоматты сақталады'}</div>
          <button className="icon-button danger" title="Себетке жіберу" onClick={trashLesson}><Trash2 size={17} /></button>
        </div>
        {error && <div className="notice error"><span>{error}</span><button className="icon-button" onClick={() => setError('')}><X size={15} /></button></div>}

        {tab === 'record' && <RecorderPanel lessonId={selected.id} onSaved={uploadRecording} onTranscribe={directTranscribe} />}
        {tab === 'text' && (
          <div className="stack">
            <section className="panel">
              <div className="panel-heading"><div><p className="eyebrow">ӨЗГЕРМЕЙТІН ТҮПНҰСҚА</p><h3>Бастапқы транскрипция</h3></div><span className="counter">{selected.transcript.length} таңба</span></div>
              <textarea className="editor-area" value={selected.transcript} onChange={(event) => updateSelected({ transcript: event.target.value })} placeholder="Whisper нәтижесі осы жерде сақталады..." />
            </section>
            <section className="panel">
              <div className="panel-heading"><div><p className="eyebrow">ӨҢДЕЛГЕН НҰСҚА</p><h3>Түзетілген мәтін</h3></div><button className="secondary" onClick={() => applyAi('clean')}><Sparkles size={16} /> AI арқылы түзету</button></div>
              <textarea className="editor-area" value={selected.cleaned_transcript} onChange={(event) => updateSelected({ cleaned_transcript: event.target.value })} placeholder="Тыныс белгілері, абзацтар және сөйлеушілер бөлінген мәтін..." />
            </section>
          </div>
        )}
        {tab === 'notes' && (
          <div className="stack">
            <section className="panel">
              <div className="panel-heading"><div><p className="eyebrow">AI КОНСПЕКТ</p><h3>Оқу материалын жасаңыз</h3></div></div>
              <div className="button-row wrap">
                <button className="primary" onClick={() => applyAi('full-summary')}>Толық конспект жасау</button>
                <button className="secondary" onClick={() => applyAi('short-summary')}>Қысқаша конспект</button>
                <button className="secondary" onClick={() => applyAi('quiz')}>Тест жасау</button>
                <button className="secondary" onClick={() => applyAi('flashcards')}>Карточкалар жасау</button>
                <button className="ghost" onClick={() => applyAi('explain')}>Қарапайым тілмен түсіндіру</button>
              </div>
            </section>
            <section className="panel"><h3>Толық конспект</h3><textarea className="editor-area" value={selected.summary} onChange={(event) => updateSelected({ summary: event.target.value })} placeholder="AI конспекті..." /></section>
            {selected.short_summary && <section className="panel prose"><h3>Қысқаша конспект</h3><p>{selected.short_summary}</p></section>}
            {!!selected.key_points.length && <section className="panel prose"><h3>Негізгі ойлар</h3><ol>{selected.key_points.map((point, index) => <li key={index}>{point}</li>)}</ol></section>}
            {!!selected.terms.length && <section className="panel"><h3>Терминдер</h3><div className="term-grid">{selected.terms.map((item) => <article key={item.term}><strong>{item.term}</strong><p>{item.definition}</p></article>)}</div></section>}
            {!!selected.quiz.length && <section className="panel"><h3>Тест</h3><div className="stack">{selected.quiz.map((item, index) => <details key={index}><summary>{index + 1}. {item.question}</summary><ul>{item.options.map((option) => <li key={option}>{option}</li>)}</ul><strong>Жауабы: {item.answer}</strong></details>)}</div></section>}
            {!!selected.flashcards.length && <section className="panel"><h3>Карточкалар</h3><div className="term-grid">{selected.flashcards.map((card, index) => <article key={index}><strong>{card.front}</strong><p>{card.back}</p></article>)}</div></section>}
          </div>
        )}
        {tab === 'files' && <FileManager lessonId={selected.id} userId={userId} files={files} language={language} model={model} onFilesChanged={refreshFiles} onTranscript={async (text) => { updateSelected({ transcript: text }); setTab('text') }} onPdfText={(text, name, pages) => { setPdfText(text); setPdfName(name); setPdfPages(pages); setPdfPage(1); setTab('books') }} />}
        {tab === 'books' && (
          <div className="stack">
            <FileManager lessonId={selected.id} userId={userId} files={files.filter((file) => file.file_type === 'application/pdf' || file.file_name.toLowerCase().endsWith('.pdf'))} language={language} model={model} onFilesChanged={refreshFiles} onTranscript={async (text) => updateSelected({ transcript: text })} onPdfText={(text, name, pages) => { setPdfText(text); setPdfName(name); setPdfPages(pages); setPdfPage(1) }} />
            {pdfText && <section className="panel"><div className="panel-heading"><div><p className="eyebrow">PDF МӘТІНІ</p><h3>{pdfName}</h3></div><button className="secondary" onClick={summarizePdfSection}>Осы бөлімнен конспект</button></div><div className="search-box"><Search size={17} /><input value={pdfSearch} onChange={(event) => setPdfSearch(event.target.value)} placeholder="Кітап ішінен іздеу..." /></div>{!pdfSearch && pdfPages.length > 0 && <div className="pdf-pagination"><button className="ghost" disabled={pdfPage <= 1} onClick={() => setPdfPage((page) => page - 1)}>Алдыңғы бет</button><strong>{pdfPage} / {pdfPages.length} бет</strong><button className="ghost" disabled={pdfPage >= pdfPages.length} onClick={() => setPdfPage((page) => page + 1)}>Келесі бет</button></div>}<textarea className="editor-area tall" value={displayedPdfText} readOnly /><div className="book-question"><input value={pdfQuestion} onChange={(event) => setPdfQuestion(event.target.value)} placeholder="Осы бет немесе іздеу нәтижесі бойынша сұрақ қойыңыз..." /><button className="primary" disabled={!pdfQuestion.trim()} onClick={askPdf}>Кітаптан сұрау</button></div>{pdfAnswer && <div className="notice info">{pdfAnswer}</div>}</section>}
          </div>
        )}
        {tab === 'listen' && <TextToSpeech text={selected.cleaned_transcript || selected.summary || selected.transcript || pdfText} />}
      </section>
    )
  }

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNav ? 'open' : ''}`}>
        <div className="sidebar-brand"><div className="brand-mark small-mark"><Sparkles /></div><div><strong>Сабақ AI</strong><span>Ақылды оқу жүйесі</span></div><button aria-label="Мәзірді жабу" className="mobile-close" onClick={() => setMobileNav(false)}><X /></button></div>
        <button className="new-lesson" onClick={addLesson}><Plus size={19} /> Жаңа сабақ</button>
        <nav>{navItems.map((item) => <button key={item.id} className={tab === item.id ? 'active' : ''} onClick={() => { setTab(item.id); setMobileNav(false) }}>{item.icon}<span>{item.label}</span>{item.id === 'trash' && deletedLessons.length > 0 && <em>{deletedLessons.length}</em>}</button>)}</nav>
        <div className="sidebar-bottom">
          <div className={`cloud-state ${navigator.onLine ? '' : 'offline'}`}>{navigator.onLine ? <Gauge size={17} /> : <WifiOff size={17} />}<span>{isCloudConfigured ? (navigator.onLine ? 'Cloud sync қосулы' : 'Offline режим') : 'Setup режимі'}</span></div>
        </div>
      </aside>
      {mobileNav && <button className="nav-overlay" aria-label="Мәзір сыртындағы фон" onClick={() => setMobileNav(false)} />}
      <main className="main-area">
        <header className="topbar">
          <button aria-label="Мәзірді ашу" className="menu-button" onClick={() => setMobileNav(true)}><Menu /></button>
          {selected && tab !== 'overview' && <button className="back-button" onClick={() => setTab('overview')}><ChevronLeft size={18} /> Сабақтар</button>}
          <div className="topbar-spacer" />
          <span className="autosave"><Check size={15} /> Autosave</span>
        </header>
        {loading ? <div className="loading-screen inline"><div className="brand-mark"><Sparkles /></div><p>Сабақтар жүктелуде...</p></div> : renderContent()}
      </main>
    </div>
  )
}

export default App
